import { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Input,
  Empty,
  Tooltip,
  Tag,
  Select
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  TagOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { internalCustomPlans } from '../../api/internalAdminEndpoints';
import { useAppNotifications } from '../../utils/notify';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import { formatDateTime } from '../../utils/format';
import useInternalAdminAuth from '../../contexts/InternalAdminAuthContext';
import StatCard from '../../components/internalAdmin/StatCard';
import { paiseToINR } from '../../components/internalAdmin/AccountCells';

// Stage 5N-3: Custom Plans list page.
//
// One row = one (purchase_plans + plan_coupons) pair, treated as a
// single Custom Plan in the operator UI. Filter / search / status
// chips mirror the Pending Accounts surface so the look stays
// consistent across the internal-admin surface.

const { TextArea } = Input;

function StatusTag({ plan }) {
  if (!plan) return <Tag>—</Tag>;
  const now = Date.now();
  const expired = plan.validUntil && new Date(plan.validUntil).getTime() <= now;
  if (expired) return <Tag color="default">Expired</Tag>;
  if (plan.isActive) return <Tag color="green">Active</Tag>;
  return <Tag color="red">Inactive</Tag>;
}

function ValidityCell({ plan }) {
  if (!plan) return <span className="text-ink-400">—</span>;
  if (!plan.validFrom && !plan.validUntil) {
    return <span className="text-xs text-ink-500">No window</span>;
  }
  return (
    <div className="leading-tight text-xs">
      <p className="m-0 text-ink-700">
        From {plan.validFrom ? formatDateTime(plan.validFrom) : '—'}
      </p>
      <p className="m-0 text-ink-500">
        Until {plan.validUntil ? formatDateTime(plan.validUntil) : '—'}
      </p>
    </div>
  );
}

function RestrictionsCell({ plan }) {
  if (!plan) return <span className="text-ink-400">—</span>;
  const bits = [];
  if (plan.restrictedGstin) bits.push({ label: 'GSTIN', value: plan.restrictedGstin });
  if (plan.restrictedEmail) bits.push({ label: 'Email', value: plan.restrictedEmail });
  if (plan.restrictedMobile) bits.push({ label: 'Mobile', value: plan.restrictedMobile });
  if (bits.length === 0) {
    return <span className="text-xs text-ink-500">Open</span>;
  }
  return (
    <div className="leading-tight text-xs min-w-0">
      {bits.map((b) => (
        <p key={b.label} className="m-0 truncate" title={`${b.label}: ${b.value}`}>
          <span className="text-ink-500">{b.label}:</span>{' '}
          <span className="text-ink-800">{b.value}</span>
        </p>
      ))}
    </div>
  );
}

export default function CustomPlansListPage() {
  const { hasPermission } = useInternalAdminAuth();
  const { notifySuccess, notifyError } = useAppNotifications();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [activateTarget, setActivateTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const canCreate     = hasPermission('custom_plans.create');
  const canUpdate     = hasPermission('custom_plans.update');
  const canActivate   = hasPermission('custom_plans.activate');
  const canDeactivate = hasPermission('custom_plans.deactivate');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const resp = await internalCustomPlans.list({
        status: statusFilter,
        q: appliedSearch || '',
        limit: 100
      });
      setData({
        items: Array.isArray(resp.items) ? resp.items : [],
        total: typeof resp.total === 'number' ? resp.total : 0
      });
    } catch (err) {
      setError(err);
      notifyError(err, { title: 'Could not load Custom Plans' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, appliedSearch]);

  const stats = useMemo(() => {
    const items = data.items || [];
    let active = 0;
    let redemptions = 0;
    let revenuePaise = 0;
    for (const it of items) {
      if (it.isActive) active += 1;
      const r = Number(it.redemptionCount || 0);
      redemptions += Number.isFinite(r) ? r : 0;
      const total = Number(it.amountIncludingGstPaise || 0);
      if (Number.isFinite(total) && Number.isFinite(r)) {
        revenuePaise += total * r;
      }
    }
    return { total: data.total, active, redemptions, revenuePaise };
  }, [data]);

  function applySearch() {
    setAppliedSearch(searchText.trim());
  }
  function clearSearch() {
    setSearchText('');
    setAppliedSearch('');
  }

  async function confirmActivate() {
    if (!activateTarget) return;
    setBusy(true);
    try {
      await internalCustomPlans.activate(activateTarget.id);
      notifySuccess('Custom Plan activated', `${activateTarget.customCode} is now active.`);
      setActivateTarget(null);
      load();
    } catch (err) {
      notifyError(err, { title: 'Could not activate Custom Plan' });
    } finally {
      setBusy(false);
    }
  }

  async function confirmDeactivate() {
    if (!deactivateTarget) return;
    const reason = deactivateReason.trim();
    if (reason.length === 0) {
      notifyError(
        { code: 'VALIDATION_ERROR' },
        { title: 'Reason required', description: 'Enter a short reason for deactivation.' }
      );
      return;
    }
    setBusy(true);
    try {
      await internalCustomPlans.deactivate(deactivateTarget.id, { reason });
      notifySuccess(
        'Custom Plan deactivated',
        `${deactivateTarget.customCode} is no longer redeemable.`
      );
      setDeactivateTarget(null);
      setDeactivateReason('');
      load();
    } catch (err) {
      notifyError(err, { title: 'Could not deactivate Custom Plan' });
    } finally {
      setBusy(false);
    }
  }

  const columns = useMemo(
    () => [
      {
        title: 'Custom Code',
        key: 'customCode',
        width: 180,
        render: (_v, row) => (
          <div className="leading-tight min-w-0">
            <button
              type="button"
              onClick={() => navigate(`/internal/custom-plans/${row.id}`)}
              className="block max-w-full truncate text-left text-sm font-bold font-mono text-brand-700 hover:text-brand-500"
              title={row.customCode}
            >
              {row.customCode}
            </button>
            <p className="m-0 text-[11px] text-ink-500 truncate" title={row.purchasePlanCode}>
              {row.purchasePlanCode}
            </p>
          </div>
        )
      },
      {
        title: 'Plan Name',
        key: 'planName',
        width: 200,
        ellipsis: true,
        render: (_v, row) => (
          <div className="leading-tight">
            <p
              className="m-0 text-sm font-semibold text-ink-900 truncate"
              title={row.planName}
            >
              {row.planName}
            </p>
            {row.description ? (
              <p
                className="m-0 text-[11px] text-ink-500 truncate"
                title={row.description}
              >
                {row.description}
              </p>
            ) : null}
          </div>
        )
      },
      {
        title: 'Users',
        key: 'includedUsers',
        width: 80,
        align: 'right',
        render: (_v, row) => (
          <span className="text-sm font-medium text-ink-800">{row.includedUsers}</span>
        )
      },
      {
        title: 'Duration',
        key: 'durationDays',
        width: 100,
        render: (_v, row) => (
          <span className="text-xs text-ink-700">{row.durationDays} days</span>
        )
      },
      {
        title: 'Amount',
        key: 'amount',
        width: 130,
        align: 'right',
        render: (_v, row) => (
          <div className="leading-tight">
            <p className="m-0 text-sm font-semibold text-ink-900">
              {paiseToINR(row.amountIncludingGstPaise)}
            </p>
            <p className="m-0 text-[10px] text-ink-500">
              incl. {row.gstRatePercent}% GST
            </p>
          </div>
        )
      },
      {
        title: 'Validity',
        key: 'validity',
        width: 200,
        render: (_v, row) => <ValidityCell plan={row} />
      },
      {
        title: 'Restrictions',
        key: 'restrictions',
        width: 220,
        ellipsis: true,
        render: (_v, row) => <RestrictionsCell plan={row} />
      },
      {
        title: 'Redemptions',
        key: 'redemptions',
        width: 130,
        align: 'right',
        render: (_v, row) => (
          <div className="leading-tight">
            <p className="m-0 text-sm font-semibold text-ink-900">
              {row.redemptionCount}
              {row.maxRedemptions != null ? (
                <span className="text-xs text-ink-500"> / {row.maxRedemptions}</span>
              ) : null}
            </p>
            {row.lastRedeemedAt ? (
              <p className="m-0 text-[10px] text-ink-500" title={row.lastRedeemedAt}>
                last {formatDateTime(row.lastRedeemedAt)}
              </p>
            ) : null}
          </div>
        )
      },
      {
        title: 'Status',
        key: 'status',
        width: 100,
        render: (_v, row) => <StatusTag plan={row} />
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 240,
        fixed: 'right',
        render: (_v, row) => (
          <Space size={4} wrap={false}>
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/internal/custom-plans/${row.id}`)}
            >
              View
            </Button>
            <Tooltip title={canUpdate ? '' : 'You do not have custom_plans.update'}>
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={!canUpdate}
                onClick={() => navigate(`/internal/custom-plans/${row.id}/edit`)}
              >
                Edit
              </Button>
            </Tooltip>
            {row.publicSignupAllowed ? (
              <Tooltip
                title={canDeactivate ? '' : 'You do not have custom_plans.deactivate'}
              >
                <Button
                  size="small"
                  danger
                  icon={<PauseCircleOutlined />}
                  disabled={!canDeactivate}
                  onClick={() => {
                    setDeactivateReason('');
                    setDeactivateTarget(row);
                  }}
                >
                  Deactivate
                </Button>
              </Tooltip>
            ) : (
              <Tooltip
                title={canActivate ? '' : 'You do not have custom_plans.activate'}
              >
                <Button
                  size="small"
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  disabled={!canActivate}
                  onClick={() => setActivateTarget(row)}
                >
                  Activate
                </Button>
              </Tooltip>
            )}
          </Space>
        )
      }
    ],
    [canActivate, canDeactivate, canUpdate, navigate]
  );

  return (
    <div>
      <PageHeader
        title="Custom Plans"
        subtitle="Create and manage enterprise Custom Codes used during signup."
        actions={
          <Space>
            <Tag color="blue" className="!text-sm !px-3 !py-1">
              {data.total} {data.total === 1 ? 'plan' : 'plans'}
            </Tag>
            <Button icon={<ReloadOutlined />} onClick={load} disabled={loading}>
              Refresh
            </Button>
            <Tooltip title={canCreate ? '' : 'You do not have custom_plans.create'}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={!canCreate}
                onClick={() => navigate('/internal/custom-plans/new')}
              >
                Create Custom Plan
              </Button>
            </Tooltip>
          </Space>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard
          label="Total Custom Plans"
          value={stats.total}
          tone="blue"
          icon={<TagOutlined />}
          hint={stats.total === 1 ? 'Plan in catalog' : 'Plans in catalog'}
        />
        <StatCard
          label="Active"
          value={stats.active}
          tone="green"
          icon={<CheckCircleOutlined />}
          hint="Available for redemption"
        />
        <StatCard
          label="Redemptions"
          value={stats.redemptions}
          tone="amber"
          icon={<GiftOutlined />}
          hint="Across loaded rows"
        />
        <StatCard
          label="Revenue (loaded)"
          value={paiseToINR(stats.revenuePaise)}
          tone="slate"
          icon={<TeamOutlined />}
          hint="Sum of redeemed prices"
        />
      </div>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-ink-400" />}
          placeholder="Search code, name, GSTIN, or email"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={applySearch}
          className="!max-w-md"
          maxLength={120}
        />
        <Button type="primary" onClick={applySearch} disabled={loading}>
          Search
        </Button>
        {appliedSearch ? (
          <Button onClick={clearSearch} disabled={loading}>
            Clear
          </Button>
        ) : null}
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: 140 }}
          options={[
            { value: 'all',      label: 'All' },
            { value: 'active',   label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'expired',  label: 'Expired' }
          ]}
        />
        {appliedSearch ? (
          <Tag color="blue" className="!text-xs">
            search ~ "{appliedSearch}"
          </Tag>
        ) : null}
      </div>

      {loading ? (
        <LoadingBlock tip="Loading custom plans…" />
      ) : (
        <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
          <Table
            rowKey="id"
            dataSource={data.items}
            columns={columns}
            size="middle"
            pagination={false}
            scroll={{ x: 1500 }}
            tableLayout="fixed"
            locale={{
              emptyText: error ? (
                <div className="py-12 text-center text-ink-500 text-sm">
                  Could not load custom plans.
                </div>
              ) : (
                <div className="py-10">
                  <Empty
                    description={
                      <div className="text-ink-700">
                        <p className="text-sm font-semibold m-0">
                          {appliedSearch || statusFilter !== 'all'
                            ? 'No matching custom plans'
                            : 'No custom plans yet'}
                        </p>
                        <p className="text-xs text-ink-500 m-0 mt-1">
                          {canCreate
                            ? 'Click "Create Custom Plan" to add a new enterprise offer.'
                            : 'A Glass-i super or billing admin can create new Custom Plans.'}
                        </p>
                      </div>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
              )
            }}
          />
          {data.items.length > 0 ? (
            <div className="px-4 py-2 border-t border-surface-200 text-xs text-ink-500 flex items-center justify-between">
              <span>
                Showing {data.items.length} of {data.total} custom plan
                {data.total === 1 ? '' : 's'}
              </span>
            </div>
          ) : null}
        </div>
      )}

      <Modal
        open={Boolean(activateTarget)}
        title="Activate Custom Plan?"
        okText="Activate"
        okButtonProps={{ type: 'primary', loading: busy }}
        cancelButtonProps={{ disabled: busy }}
        onOk={confirmActivate}
        onCancel={() => (busy ? null : setActivateTarget(null))}
        maskClosable={!busy}
      >
        <p className="m-0">
          This will make{' '}
          <strong className="font-mono">
            {activateTarget && activateTarget.customCode}
          </strong>{' '}
          redeemable again on public signup.
        </p>
      </Modal>

      <Modal
        open={Boolean(deactivateTarget)}
        title="Deactivate Custom Plan?"
        okText="Deactivate"
        okButtonProps={{
          danger: true,
          loading: busy,
          disabled: deactivateReason.trim().length === 0
        }}
        cancelButtonProps={{ disabled: busy }}
        onOk={confirmDeactivate}
        onCancel={() => {
          if (busy) return;
          setDeactivateTarget(null);
          setDeactivateReason('');
        }}
        maskClosable={!busy}
        destroyOnHidden
      >
        <p className="m-0 mb-2">
          Deactivating{' '}
          <strong className="font-mono">
            {deactivateTarget && deactivateTarget.customCode}
          </strong>{' '}
          blocks new public-signup redemptions. Existing tenants are unaffected.
        </p>
        <label className="text-xs uppercase tracking-wider text-ink-500 font-semibold">
          Reason (required)
        </label>
        <TextArea
          value={deactivateReason}
          onChange={(e) => setDeactivateReason(e.target.value)}
          rows={3}
          maxLength={1000}
          placeholder="e.g. Promotion period ended"
          disabled={busy}
        />
      </Modal>
    </div>
  );
}
